import { supabase } from '../lib/supabaseClient';

/**
 * Fetch upvote counts for multiple recommendations
 * @param {Array} recommendationIds - Array of recommendation IDs
 * @returns {Object} - Object with recommendation_id as key and count as value
 */
export async function fetchUpvotes(recommendationIds) {
  try {
    if (!recommendationIds || recommendationIds.length === 0) {
      return {};
    }

    const { data, error } = await supabase
      .from('upvotes')
      .select('recommendation_id, count, count_internal')
      .in('recommendation_id', recommendationIds);

    if (error) {
      console.error('Error fetching upvotes:', error);
      return {};
    }

    // Convert array to object for easy lookup
    const upvoteMap = {};
    data.forEach(upvote => {
      upvoteMap[upvote.recommendation_id] = (upvote.count || 0) + (upvote.count_internal || 0);
    });

    return upvoteMap;
  } catch (error) {
    console.error('Error fetching upvotes:', error);
    return {};
  }
}

/**
 * Increment upvote count for a recommendation
 * @param {number} recommendationId - The recommendation ID to upvote
 * @returns {Object} - Updated upvote data or null if failed
 */
export async function incrementUpvote(recommendationId) {
  try {
    // Check if user has already voted (localStorage)
    const votedItems = getVotedItems();
    if (votedItems.includes(recommendationId.toString())) {
      console.log('User has already voted for this recommendation');
      return null;
    }

    // First, try to get existing upvote record
    const { data: existingUpvote, error: fetchError } = await supabase
      .from('upvotes')
      .select('count, count_internal')
      .eq('recommendation_id', recommendationId)
      .single();

    let result;
    if (fetchError && fetchError.code === 'PGRST116') {
      // No existing record, create new one with count 1
      const { data, error } = await supabase
        .from('upvotes')
        .insert({ recommendation_id: recommendationId, count: 1 })
        .select('count, count_internal')
        .single();
      
      if (error) {
        console.error('Error creating upvote:', error);
        return null;
      }
      result = data;
    } else if (existingUpvote) {
      // Update existing record by incrementing count and updating timestamp
      const { data, error } = await supabase
        .from('upvotes')
        .update({ 
          count: existingUpvote.count + 1,
          updated_at: new Date().toISOString()
        })
        .eq('recommendation_id', recommendationId)
        .select('count, count_internal')
        .single();
      
      if (error) {
        console.error('Error updating upvote:', error);
        return null;
      }
      result = data;
    } else {
      console.error('Unexpected error fetching upvote:', fetchError);
      return null;
    }

    // Mark as voted in localStorage
    markAsVoted(recommendationId);

    // Return result with combined count for display
    return {
      ...result,
      total_count: (result.count || 0) + (result.count_internal || 0)
    };
  } catch (error) {
    console.error('Error incrementing upvote:', error);
    return null;
  }
}

/**
 * Decrement upvote count for a recommendation (undo vote)
 * @param {number} recommendationId - The recommendation ID to remove upvote from
 * @returns {Object} - Updated upvote data or null if failed
 */
export async function decrementUpvote(recommendationId) {
  try {
    // Check if user has voted (localStorage)
    const votedItems = getVotedItems();
    if (!votedItems.includes(recommendationId.toString())) {
      console.log('User has not voted for this recommendation');
      return null;
    }

    // Get existing upvote record
    const { data: existingUpvote, error: fetchError } = await supabase
      .from('upvotes')
      .select('count, count_internal')
      .eq('recommendation_id', recommendationId)
      .single();

    if (fetchError) {
      console.error('Error fetching upvote for decrement:', fetchError);
      return null;
    }

    // Update existing record by decrementing count (minimum 0) and updating timestamp
    const newCount = Math.max(existingUpvote.count - 1, 0);
    const { data, error } = await supabase
      .from('upvotes')
      .update({ 
        count: newCount,
        updated_at: new Date().toISOString()
      })
      .eq('recommendation_id', recommendationId)
      .select('count, count_internal')
      .single();

    if (error) {
      console.error('Error decrementing upvote:', error);
      return null;
    }

    // Remove from localStorage
    removeVote(recommendationId);

    // Return result with combined count for display
    return {
      ...data,
      total_count: (data.count || 0) + (data.count_internal || 0)
    };
  } catch (error) {
    console.error('Error decrementing upvote:', error);
    return null;
  }
}

/**
 * Check if user has voted for a recommendation
 * @param {number} recommendationId - The recommendation ID to check
 * @returns {boolean} - True if user has voted
 */
export function hasUserVoted(recommendationId) {
  const votedItems = getVotedItems();
  return votedItems.includes(recommendationId.toString());
}

// LocalStorage helper functions
const VOTED_ITEMS_KEY = 'podRecs_votedItems';

function getVotedItems() {
  try {
    const items = localStorage.getItem(VOTED_ITEMS_KEY);
    return items ? JSON.parse(items) : [];
  } catch (error) {
    console.error('Error reading voted items from localStorage:', error);
    return [];
  }
}

function markAsVoted(recommendationId) {
  try {
    const votedItems = getVotedItems();
    if (!votedItems.includes(recommendationId.toString())) {
      votedItems.push(recommendationId.toString());
      localStorage.setItem(VOTED_ITEMS_KEY, JSON.stringify(votedItems));
    }
  } catch (error) {
    console.error('Error saving voted item to localStorage:', error);
  }
}

function removeVote(recommendationId) {
  try {
    const votedItems = getVotedItems();
    const updatedItems = votedItems.filter(id => id !== recommendationId.toString());
    localStorage.setItem(VOTED_ITEMS_KEY, JSON.stringify(updatedItems));
  } catch (error) {
    console.error('Error removing voted item from localStorage:', error);
  }
}
